import axios from 'axios'

import appConfig from 'configs/app.config'
import {TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY} from 'constants/api.constant'
import store from 'store'
import {clearAdminClub, setClub} from 'store/auth/adminClubSlice'
import {onSignInSuccess, onSignOutSuccess} from 'store/auth/sessionSlice'
import {setUser, userLoggedOut} from 'store/auth/userSlice'
import {clearAdminProfile, loadAdminProfile, tokenMatchesAdmin} from 'utils/adminProfileStorage'
import {getAccessTokenFromAuthPayload, getAuthPayload} from 'utils/authSession'

const BaseService = axios.create({
    baseURL: appConfig.apiPrefix,
    withCredentials: true,
    headers: {
        'Access-Control-Allow-Origin': '*',
    },
})

const getAccessToken = () => store.getState()?.auth?.session?.accessToken

const isSessionEndpoint = (url = '') => url.includes('/admin-sessions')
let refreshPromise = null

const clearSession = () => {
    clearAdminProfile()
    store.dispatch(onSignOutSuccess())
    store.dispatch(userLoggedOut())
    store.dispatch(clearAdminClub())
}

const interceptorsRequest = [
    (request) => {
        const accessToken = getAccessToken()

        if (accessToken) {
            request.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`
        }

        return request
    },
    (err) => Promise.reject(err),
]

const interceptorsResponse = [
    (response) => response.data,
    async (error) => {
        const {response, config} = error

        if (!config) {
            return Promise.reject(error)
        }

        const statusCode = response?.data?.code || response?.data?.statusCode || response?.status
        const isUnauth = statusCode === 401 || statusCode === 'UNAUTHORIZED_ERROR' || response?.status === 401

        if (isSessionEndpoint(config.url)) {
            const body = response?.data
            return Promise.reject(body && (body.code || body.statusCode || body.status) ? body : error)
        }

        if (isUnauth) {
            if (config._retry) {
                clearSession()
                return Promise.reject(response?.data || error)
            }
            config._retry = true
            const currentToken = getAccessToken()
            if (currentToken && config.headers?.[REQUEST_HEADER_AUTH_KEY] !== `${TOKEN_TYPE}${currentToken}`) {
                config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${currentToken}`
                return BaseService(config)
            }
            if (!refreshPromise) {
                refreshPromise = BaseService.put('/admin-sessions').then((refreshResponse) => {
                    const accessToken = getAccessTokenFromAuthPayload(getAuthPayload(refreshResponse))
                    const persistedProfile = loadAdminProfile()
                    const persistedUser = persistedProfile?.user
                    const user = store.getState()?.auth?.user || persistedUser
                    if (!accessToken || !tokenMatchesAdmin(accessToken, user) ||
                        (persistedUser && !tokenMatchesAdmin(accessToken, persistedUser))) {
                        clearSession()
                        throw new Error('Admin identity changed after refresh')
                    }
                    if (!store.getState()?.auth?.user) {
                        store.dispatch(setUser(user))
                        if (persistedProfile?.club) store.dispatch(setClub(persistedProfile.club))
                    }
                    store.dispatch(onSignInSuccess({accessToken}))
                    return accessToken
                }).catch((refreshError) => {
                    const refreshStatus = refreshError?.code || refreshError?.statusCode || refreshError?.status || refreshError?.response?.status
                    if (refreshStatus === 401 || refreshStatus === 403 || refreshStatus === 'UNAUTHORIZED_ERROR' || refreshStatus === 'FORBIDDEN_ERROR') clearSession()
                    throw refreshError
                }).finally(() => { refreshPromise = null })
            }
            try {
                const accessToken = await refreshPromise
                config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`
                return BaseService(config)
            } catch (refreshError) {
                return Promise.reject(refreshError)
            }
        }

        return Promise.reject(response?.data || error)
    },
]

BaseService.interceptors.request.use(...interceptorsRequest)
BaseService.interceptors.response.use(...interceptorsResponse)

export default BaseService
